import { BehaviorSubject, forkJoin, merge, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, map, takeUntil } from 'rxjs/operators';

import { Injectable, OnDestroy } from '@angular/core';

import { ISnListResponse, ISnMapResponse } from '@lib/api';
import { SnMapClusterPoint } from '@lib/api';
import { SnCamera, SnCameraDisposition } from '@lib/api/entities';
import { CameraOwnership, OWNERSHIP_GROUP, STATUS_GROUP } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SnLiveUser } from '@lib/live-tracking/models';

import { CamerasServiceInterface, CameraMapQueryParams, CameraQueryParams } from '../interfaces';

export interface CameraLabelData {
  text: string;
  position: [number, number];
}

@Injectable({
  providedIn: 'root',
})
export class CamerasService implements CamerasServiceInterface, OnDestroy {
  private static readonly MAX_LABEL_LENGTH = 11;
  private static readonly TRUNCATED_LABEL_LENGTH = 9;
  private static readonly DEFAULT_LABEL_COORDINATE_PRECISION = 6;

  private readonly _hovered$ = new Subject<SnCamera>();
  private readonly _cameraSelected$ = new Subject<SnCamera>();
  private readonly _selectedUsers$ = new Subject<{ users: SnLiveUser[]; channelType: string }>();
  private readonly _firstTwoClicked$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();
  private readonly _cancelDocking$ = new Subject<void>();

  private readonly _cameras$ = new BehaviorSubject<SnCamera[]>([]);
  private readonly _clusteredCameras$ = new BehaviorSubject<SnMapClusterPoint[]>([]);
  private readonly _fetchTrigger$ = new Subject<CameraQueryParams>();

  private _clustering: boolean = true;
  private _camerasParam: string = '';
  private _isOwned: boolean | undefined = undefined;
  private _pendingCameras: string[] = [];
  private _deselectedCameraGroupsParam: string = '';
  private readonly _neverClusterMakes: readonly string[] = ['Skydio'];

  public get hovered$(): Observable<SnCamera> {
    return this._hovered$.asObservable();
  }

  public get cameraSelected$(): Observable<SnCamera> {
    return this._cameraSelected$.asObservable();
  }

  public get selectedUsers$(): Observable<{ users: SnLiveUser[]; channelType: string }> {
    return this._selectedUsers$.asObservable();
  }

  public get firstTwoClicked$(): Observable<void> {
    return this._firstTwoClicked$.asObservable();
  }

  public get cameras$(): Observable<SnCamera[]> {
    return this._cameras$.asObservable();
  }

  public get clusteredCameras$(): Observable<SnMapClusterPoint[]> {
    return this._clusteredCameras$.asObservable();
  }

  public get cancelDocking$(): Observable<void> {
    return this._cancelDocking$.asObservable();
  }

  constructor(private readonly _camerasAPI: SnCamerasAPI) {
    this._fetchTrigger$
      .pipe(debounceTime(100), takeUntil(this._destroy$))
      .subscribe((queryParams) => {
        this._fetchCameraData(queryParams);
      });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._hovered$.complete();
    this._cameraSelected$.complete();
    this._selectedUsers$.complete();
    this._firstTwoClicked$.complete();
    this._fetchTrigger$.complete();
    this._cameras$.complete();
    this._clusteredCameras$.complete();
    this._cancelDocking$.complete();
  }

  public getCamera(cameraId: string): Observable<SnCamera> {
    return this._camerasAPI.getCamera(cameraId).pipe(takeUntil(this._destroy$));
  }

  public setClustering(enabled: boolean): void {
    this._clustering = enabled;
  }

  public getClustering(): boolean {
    return this._clustering;
  }

  public getCamerasParameter(params: string[]): void {
    const cameraStatus = params.filter((item) => STATUS_GROUP.includes(item));
    const cameraOwnership = params.filter((item) => OWNERSHIP_GROUP.includes(item));
    const includesMyCameras = cameraOwnership.includes(CameraOwnership.MyCameras);
    const includesSharedWithMe = cameraOwnership.includes(CameraOwnership.SharedWithMe);

    this._camerasParam = cameraStatus.join(',');
    if (includesMyCameras && !includesSharedWithMe) {
      this._isOwned = true;
    } else if (includesSharedWithMe && !includesMyCameras) {
      this._isOwned = false;
    } else {
      this._isOwned = undefined;
    }
  }

  public getCamerasParam(): string {
    return this._camerasParam;
  }

  public setDeselectedCameraGroupsParam(value: string): void {
    this._deselectedCameraGroupsParam = value || '';
  }

  public getDeselectedCameraGroupsParam(): string {
    return this._deselectedCameraGroupsParam;
  }

  public isSkydioCamera(camera: SnCamera | { camera?: SnCamera; make?: string }): boolean {
    if ('camera' in camera && camera.camera) {
      return camera.camera.make?.toLowerCase() === 'skydio';
    }
    if ('make' in camera && camera.make) {
      return camera.make.toLowerCase() === 'skydio';
    }
    return false;
  }

  public isDownDroneCamera(camera: SnCamera): boolean {
    if (!camera?.make || !camera?.status) {
      return false;
    }

    const droneManufacturers = ['skydio', 'airdata'];
    const isDroneManufacturer = droneManufacturers.includes(camera.make.toLowerCase());
    const isDownStatus = camera.status.toLowerCase() === 'down';

    return isDroneManufacturer && isDownStatus;
  }

  public formatCameraLabel(name: string, count: number): string {
    if (!name) {
      return count > 1 ? `+${count - 1}` : '';
    }

    const truncated =
      name.length <= CamerasService.MAX_LABEL_LENGTH
        ? name
        : `${name.substring(0, CamerasService.TRUNCATED_LABEL_LENGTH)}..`;

    return count > 1 ? `${truncated} +${count - 1}` : truncated;
  }

  public prepareCameraLabelData(
    cameras: SnCamera[],
    coordinatePrecision: number = CamerasService.DEFAULT_LABEL_COORDINATE_PRECISION,
  ): CameraLabelData[] {
    if (!cameras?.length) {
      return [];
    }

    // We need to dedupe labels at the same position so they don't overlap.
    // However, it needs to match the camera icon actually being displayed by DeckGL to match the click and hover label
    // This seems to be the last camera seen at that position.

    const positionMap = new Map<string, { lastSeenCamera: SnCamera; seenCount: number }>();

    cameras.forEach((camera) => {
      if (!camera?.name || camera?.geometry?.coordinates?.length < 2) {
        return;
      }

      const [longitude, latitude] = camera.geometry.coordinates;
      const posKey = `${longitude.toFixed(coordinatePrecision)},${latitude.toFixed(
        coordinatePrecision,
      )}`;

      const seen = positionMap.get(posKey);

      if (seen) {
        seen.lastSeenCamera = camera;
        seen.seenCount++;
      } else {
        positionMap.set(posKey, {
          lastSeenCamera: camera,
          seenCount: 1,
        });
      }
    });

    // Precompute text and position for each unique location
    const labelData: CameraLabelData[] = [];

    positionMap.forEach(({ lastSeenCamera, seenCount }) => {
      const [longitude, latitude] = lastSeenCamera.geometry.coordinates;

      labelData.push({
        text: this.formatCameraLabel(lastSeenCamera.name, seenCount),
        position: [longitude, latitude],
      });
    });

    return labelData;
  }

  public getPendingCameras(): string[] {
    return [...this._pendingCameras];
  }

  public setPendingCameras(cameras: string[]): void {
    this._pendingCameras = [...cameras];
  }

  public getNeverClusterMakes(): readonly string[] {
    return this._neverClusterMakes;
  }

  public emitCameraSelected(camera: SnCamera | null): void {
    this._cameraSelected$.next(camera);
  }

  public emitCameraHovered(camera: SnCamera | null): void {
    this._hovered$.next(camera);
  }

  public emitSelectedUsers(users: SnLiveUser[], channelType: string): void {
    this._selectedUsers$.next({ users, channelType });
  }

  public emitFirstTwoClicked(): void {
    this._firstTwoClicked$.next();
  }

  public getDescendantRequests(cameras: SnCamera[]): Observable<ISnListResponse<SnCamera>>[] {
    return cameras
      .filter((camera) => camera.isParentCamera)
      .map((parent) => {
        return this._camerasAPI.getDescendantList(parent.id).pipe(takeUntil(this._destroy$));
      });
  }

  public handleParentCamera(camera: SnCamera): Observable<SnCamera[]> {
    return this._camerasAPI.getDescendantList(camera.id).pipe(
      map((response) => response.results || []),
      takeUntil(this._destroy$),
    );
  }

  public dockCameras(cameraIds: string[]): Observable<SnCamera | null> {
    this.setPendingCameras(cameraIds || []);
    if (!cameraIds?.length) {
      return of(null);
    }

    const cameraObservables = cameraIds.map((cameraId) =>
      this._camerasAPI
        .getCamera(cameraId)
        .pipe(takeUntil(this._cancelDocking$), takeUntil(this._destroy$)),
    );

    return merge(...cameraObservables).pipe(
      map((camera) => {
        this.emitCameraSelected(camera);
        const pending = this.getPendingCameras();
        const index = pending.indexOf(camera.id);
        if (index > -1) {
          const updated = [...pending];
          updated.splice(index, 1);
          this.setPendingCameras(updated);
        }
        return camera;
      }),
    );
  }

  public cancelDocking(): void {
    this._cancelDocking$.next();
  }

  public initCameras(params: CameraQueryParams): void {
    this._fetchTrigger$.next(params);
  }

  public updateMapBounds(
    mapQueryParams: Partial<CameraMapQueryParams>,
    cameraGroupFilter?: string,
    maxZoom?: number,
  ): void {
    if (cameraGroupFilter !== undefined) {
      this._deselectedCameraGroupsParam = cameraGroupFilter;
    }
    const currentParams: CameraQueryParams = {
      mapQueryParams,
      cameraGroupFilter: this._deselectedCameraGroupsParam,
      maxZoom,
    };
    this._fetchTrigger$.next(currentParams);
  }

  private _fetchCameraData(queryParams: CameraQueryParams): void {
    const hasMapBounds =
      queryParams.mapQueryParams?.pageMapLeftTop && queryParams.mapQueryParams?.pageMapRightBottom;

    if (!hasMapBounds) {
      return;
    }

    const currentZoom = queryParams.mapQueryParams?.pageMapZoomLevel || 0;
    const maxZoom = queryParams.maxZoom || 0;
    const isHighZoom = currentZoom >= 19 || (maxZoom > 0 && currentZoom === maxZoom);
    const groupFilter = queryParams.cameraGroupFilter || '';
    const excludedMakes = this._neverClusterMakes.join(',');

    let request$: Observable<ISnMapResponse<SnCamera>>;

    const pageMapLeftTop = queryParams.mapQueryParams.pageMapLeftTop || '';
    const pageMapRightBottom = queryParams.mapQueryParams.pageMapRightBottom || '';

    if (isHighZoom || !this._clustering) {
      request$ = this._camerasAPI.getCameraMapBrief({
        disposition: SnCameraDisposition.External,
        pageMapLeftTop,
        pageMapRightBottom,
        status_In: this._camerasParam,
        exclude_child_cameras: true,
        camera_group__not_in: groupFilter,
        ...(this._isOwned !== undefined && { isOwned: this._isOwned }),
      });
    } else {
      request$ = forkJoin([
        this._camerasAPI
          .getCameraMap({
            disposition: SnCameraDisposition.External,
            pageMapClustered: true,
            pageMapGridSize: 60,
            pageMapZoomLevel: currentZoom,
            pageMapLeftTop,
            pageMapRightBottom,
            status_In: this._camerasParam,
            camera_group__not_in: groupFilter,
            make__not__in: excludedMakes,
            ...(this._isOwned !== undefined && { isOwned: this._isOwned }),
          })
          .pipe(catchError(() => of({ results: [], clusterResults: [] }))),
        this._camerasAPI
          .getCameraMap({
            disposition: SnCameraDisposition.External,
            pageMapLeftTop,
            pageMapRightBottom,
            status_In: this._camerasParam,
            exclude_child_cameras: true,
            camera_group__not_in: groupFilter,
            make__in: excludedMakes,
            ...(this._isOwned !== undefined && { isOwned: this._isOwned }),
          })
          .pipe(catchError(() => of({ results: [], clusterResults: [] }))),
      ]).pipe(
        map(([clusteredResponse, individualResponse]) => ({
          results: [...clusteredResponse.results, ...individualResponse.results],
          clusterResults: clusteredResponse.clusterResults,
        })),
      );
    }

    request$
      .pipe(
        catchError(() => {
          const empty: ISnMapResponse<SnCamera> = {
            results: [],
            clusterResults: [],
          };
          return of(empty);
        }),
        takeUntil(this._destroy$),
      )
      .subscribe(({ results, clusterResults }) => {
        this._cameras$.next(results);
        this._clusteredCameras$.next(clusterResults || []);
      });
  }
}
